import { VoxelAsset, VoxelData } from '../voxelTypes';
import { PALETTE, getRandomizedColor } from '../voxelColor';

export type TreeType = 'oak' | 'pine' | 'autumn_birch' | 'willow' | 'apple_tree' | 'cherry_blossom' | 'christmas_tree';

export function generateVoxelTree(
  id: string,
  name: string,
  type: TreeType = 'oak'
): VoxelAsset {
  const voxels: VoxelData[] = [];

  if (type === 'oak') {
    // High resolution majestic oak tree with thick fluted trunk, roots, branching arms, and dense layered canopy
    const trunkHeight = 14;
    const trunkRadius = 2.2;

    // Fluted Trunk & Roots
    for (let y = 0; y < trunkHeight; y++) {
      const r = trunkRadius * (1.0 - (y / trunkHeight) * 0.4);
      for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          if (x * x + z * z <= r * r) {
            const barkColor = getRandomizedColor(PALETTE.WOOD_OAK_DARK, 12);
            voxels.push({ x, y, z, color: barkColor, materialType: 'opaque' });
          }
        }
      }
    }

    // Spreading Buttress Roots at ground level
    const rootAngles = [0, 1.2, 2.5, 3.8, 5.1];
    rootAngles.forEach(angle => {
      for (let dist = 2; dist <= 5; dist++) {
        const rx = Math.round(Math.cos(angle) * dist);
        const rz = Math.round(Math.sin(angle) * dist);
        const ry = Math.max(0, 3 - dist);
        voxels.push({ x: rx, y: ry, z: rz, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });
      }
    });

    // Spreading Canopy Branches
    const branches = [
      { bx: -4, by: trunkHeight - 4, bz: -2, dx: -2, dz: -1 },
      { bx: 4, by: trunkHeight - 3, bz: 2, dx: 2, dz: 1 },
      { bx: 1, by: trunkHeight - 2, bz: -4, dx: 1, dz: -2 },
      { bx: -2, by: trunkHeight - 1, bz: 4, dx: -1, dz: 2 },
    ];
    branches.forEach(b => {
      for (let i = 0; i <= 4; i++) {
        voxels.push({
          x: Math.round(b.dx * (i / 4)),
          y: b.by + Math.floor(i / 2),
          z: Math.round(b.dz * (i / 4)),
          color: PALETTE.WOOD_OAK_MEDIUM,
          materialType: 'opaque'
        });
      }
    });

    // Multi-Sphere Organic Leaf Clusters
    const canopySpheres = [
      { cx: 0, cy: trunkHeight + 4, cz: 0, r: 7.0, baseColor: PALETTE.LEAF_OAK_LUSH },
      { cx: -5, cy: trunkHeight + 1, cz: -3, r: 5.5, baseColor: PALETTE.LEAF_OAK_LIGHT },
      { cx: 5, cy: trunkHeight + 2, cz: 3, r: 5.5, baseColor: PALETTE.LEAF_OAK_LUSH },
      { cx: 2, cy: trunkHeight - 1, cz: 5, r: 5.0, baseColor: PALETTE.GRASS_BRIGHT },
      { cx: -3, cy: trunkHeight + 5, cz: 4, r: 4.8, baseColor: PALETTE.LEAF_OAK_LIGHT },
    ];

    canopySpheres.forEach(sphere => {
      for (let x = -Math.ceil(sphere.r); x <= Math.ceil(sphere.r); x++) {
        for (let y = -Math.ceil(sphere.r); y <= Math.ceil(sphere.r); y++) {
          for (let z = -Math.ceil(sphere.r); z <= Math.ceil(sphere.r); z++) {
            const dist = Math.hypot(x, y, z);
            if (dist <= sphere.r) {
              // Edge noise for leafy silhouette
              if (dist > sphere.r - 1.2 && Math.random() < 0.25) continue;

              const color = getRandomizedColor(sphere.baseColor, 0.03);
              voxels.push({
                x: sphere.cx + x,
                y: sphere.cy + y,
                z: sphere.cz + z,
                color,
                materialType: 'foliage'
              });
            }
          }
        }
      }
    });

  } else if (type === 'pine') {
    // Tall Conical Alpine Evergreen Pine
    const trunkHeight = 22; // Central trunk extends continuously up through canopy

    for (let y = 0; y <= trunkHeight; y++) {
      const r = y < 10 ? 1 : 0; // Slightly thicker base, tapered top core
      for (let x = -r; x <= r; x++) {
        for (let z = -r; z <= r; z++) {
          if (x * x + z * z <= r * r) {
            voxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.WOOD_PINE_BARK, 0.02),
              materialType: 'opaque'
            });
          }
        }
      }
    }

    // Tiered Conical Needle Boughs (from y = 4 up to y = 20)
    for (let y = 4; y <= 20; y++) {
      // Calculate smooth tapering radius from 7 down to 0 at top peak
      const progress = (y - 4) / 16; // 0.0 to 1.0
      const currentR = Math.max(0, 7.0 * (1.0 - progress));

      if (currentR <= 0.2) continue;

      for (let x = -Math.ceil(currentR); x <= Math.ceil(currentR); x++) {
        for (let z = -Math.ceil(currentR); z <= Math.ceil(currentR); z++) {
          const d = Math.hypot(x, z);
          if (d <= currentR && d > 0) { // Don't overwrite central trunk
            if (d > currentR - 0.8 && Math.random() < 0.2) continue; // Natural edge variation
            voxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.LEAF_PINE_DARK, 0.03),
              materialType: 'foliage'
            });
          }
        }
      }
    }

    // Top tip peak needles (attached directly to trunk peak)
    voxels.push({ x: 0, y: 21, z: 0, color: PALETTE.LEAF_PINE_DARK, materialType: 'foliage' });
    voxels.push({ x: 0, y: 22, z: 0, color: PALETTE.LEAF_PINE_DARK, materialType: 'foliage' });

  } else if (type === 'apple_tree') {
    // Orchard Apple Tree with Fruit Voxels
    const trunkHeight = 10;
    for (let y = 0; y < trunkHeight; y++) {
      voxels.push({ x: 0, y, z: 0, color: PALETTE.WOOD_OAK_MEDIUM, materialType: 'opaque' });
      if (y < 4) voxels.push({ x: 1, y, z: 0, color: PALETTE.WOOD_OAK_DARK, materialType: 'opaque' });
    }

    const r = 6.0;
    for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
      for (let y = -Math.ceil(r); y <= Math.ceil(r); y++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          const dist = Math.hypot(x, y, z);
          if (dist <= r) {
            const isApple = Math.random() < 0.07 && dist > 2.5;
            voxels.push({
              x,
              y: trunkHeight + y + 2,
              z,
              color: isApple ? PALETTE.FLOWER_RED : getRandomizedColor(PALETTE.LEAF_OAK_LUSH, 0.03),
              materialType: isApple ? 'emissive' : 'foliage'
            });
          }
        }
      }
    }

  } else if (type === 'autumn_birch') {
    // Birch with smooth light bark & warm golden autumn foliage
    const trunkHeight = 16;
    for (let y = 0; y < trunkHeight; y++) {
      const isKnot = (y % 4 === 0) && Math.random() > 0.6;
      voxels.push({
        x: 0, y, z: 0,
        color: isKnot ? PALETTE.WOOD_OAK_DARK : 0xd8d4c8,
        materialType: 'opaque'
      });
      voxels.push({
        x: 1, y, z: 0,
        color: isKnot ? PALETTE.WOOD_OAK_DARK : 0xd8d4c8,
        materialType: 'opaque'
      });
    }

    const r = 5.5;
    for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
      for (let y = -Math.ceil(r); y <= Math.ceil(r); y++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          if (Math.hypot(x, y, z) <= r) {
            const isGolden = Math.random() > 0.35;
            voxels.push({
              x,
              y: trunkHeight + y + 1,
              z,
              color: isGolden ? getRandomizedColor(PALETTE.LEAF_AUTUMN_GOLD, 0.03) : getRandomizedColor(PALETTE.LEAF_AUTUMN_RED, 0.03),
              materialType: 'foliage'
            });
          }
        }
      }
    }
  } else if (type === 'cherry_blossom') {
    // Beautiful gnarled Cherry Blossom tree with soft pink and white organic petal clusters
    const trunkHeight = 11;
    const trunkRadius = 1.4;

    // Build main gnarled trunk
    for (let y = 0; y < trunkHeight; y++) {
      // Slightly lean the trunk for an organic, winding shape
      const dx = y < 4 ? 0 : y < 8 ? -1 : -2;
      const dz = y < 6 ? 0 : 1;
      const r = trunkRadius * (1.0 - (y / trunkHeight) * 0.35);

      for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
        for (let z = -Math.ceil(r); z <= Math.ceil(r); z++) {
          if (x * x + z * z <= r * r) {
            voxels.push({
              x: x + dx,
              y,
              z: z + dz,
              color: getRandomizedColor(PALETTE.WOOD_OAK_DARK, 0.02),
              materialType: 'opaque'
            });
          }
        }
      }
    }

    // Crooked wooden branches extending outwards from the trunk
    const branches = [
      // Branch 1: Left and slightly forward
      { startY: 5, path: [{ x: -1, y: 5, z: 0 }, { x: -2, y: 6, z: 0 }, { x: -3, y: 7, z: -1 }, { x: -5, y: 8, z: -2 }] },
      // Branch 2: Right and slightly backward
      { startY: 6, path: [{ x: 1, y: 6, z: 1 }, { x: 2, y: 7, z: 2 }, { x: 4, y: 8, z: 2 }, { x: 5, y: 9, z: 3 }] },
      // Branch 3: Forward-Right
      { startY: 7, path: [{ x: -1, y: 7, z: 2 }, { x: -1, y: 8, z: 3 }, { x: 0, y: 9, z: 4 }, { x: 1, y: 10, z: 5 }] },
      // Branch 4: Backward-Left
      { startY: 8, path: [{ x: -2, y: 8, z: -1 }, { x: -3, y: 9, z: -2 }, { x: -4, y: 10, z: -3 }, { x: -5, y: 11, z: -4 }] },
      // Branch 5: High top cluster support
      { startY: 9, path: [{ x: -2, y: 9, z: 1 }, { x: -2, y: 10, z: 2 }, { x: -1, y: 11, z: 2 }] }
    ];

    branches.forEach(b => {
      b.path.forEach(pt => {
        voxels.push({
          x: pt.x,
          y: pt.y,
          z: pt.z,
          color: getRandomizedColor(PALETTE.WOOD_OAK_DARK, 0.01),
          materialType: 'opaque'
        });
      });
    });

    // Spreading clusters of pink blossoms on the branch ends and limbs
    const blossomSpheres = [
      { cx: -5, cy: 9, cz: -2, r: 3.5, baseColor: 0xffb7c5 }, // left tip cluster
      { cx: 5, cy: 10, cz: 3, r: 3.5, baseColor: 0xffccd5 },  // right tip cluster
      { cx: 1, cy: 11, cz: 5, r: 3.5, baseColor: 0xffb7c5 },  // forward tip cluster
      { cx: -5, cy: 12, cz: -4, r: 3.2, baseColor: 0xff8da1 }, // backward tip cluster
      { cx: -1, cy: 12, cz: 2, r: 4.0, baseColor: 0xffccd5 },  // top crown cluster
      
      // Mid-branch blossoms to cluster around limbs
      { cx: -2.5, cy: 7, cz: -0.5, r: 2.2, baseColor: 0xff8da1 },
      { cx: 3.0, cy: 8, cz: 2.0, r: 2.4, baseColor: 0xffb7c5 },
      { cx: -3.0, cy: 9.5, cz: -2.5, r: 2.2, baseColor: 0xffccd5 }
    ];

    blossomSpheres.forEach(sphere => {
      for (let x = -Math.ceil(sphere.r); x <= Math.ceil(sphere.r); x++) {
        for (let y = -Math.ceil(sphere.r); y <= Math.ceil(sphere.r); y++) {
          for (let z = -Math.ceil(sphere.r); z <= Math.ceil(sphere.r); z++) {
            const dist = Math.hypot(x, y, z);
            if (dist <= sphere.r) {
              if (dist > sphere.r - 0.7 && Math.random() < 0.25) continue; // organic edges
              
              const px = sphere.cx + x;
              const py = sphere.cy + y;
              const pz = sphere.cz + z;

              // Avoid overwriting branch skeleton wood voxels to keep limbs visible!
              const isWood = voxels.some(v => v.x === px && v.y === py && v.z === pz && v.materialType === 'opaque');
              if (!isWood) {
                voxels.push({
                  x: px,
                  y: py,
                  z: pz,
                  color: getRandomizedColor(sphere.baseColor, 0.03),
                  materialType: 'foliage'
                });
              }
            }
          }
        }
      }
    });

  } else if (type === 'christmas_tree') {
    // Elegant Christmas tree (based on pine) with a glowing golden star topper and festive ornaments
    const trunkHeight = 12; // Stop trunk early so it doesn't poke out of the top!

    for (let y = 0; y < trunkHeight; y++) {
      const r = y < 10 ? 1 : 0; // Slightly thicker base, tapered top core
      for (let x = -r; x <= r; x++) {
        for (let z = -r; z <= r; z++) {
          if (x * x + z * z <= r * r) {
            voxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.WOOD_PINE_BARK, 0.02),
              materialType: 'opaque'
            });
          }
        }
      }
    }

    // Tiered Conical Needle Boughs (from y = 4 up to y = 20)
    for (let y = 4; y <= 20; y++) {
      const progress = (y - 4) / 16; // 0.0 to 1.0
      // Maintain a minimum radius of 1.3 near the top to completely eliminate empty gap layers!
      const currentR = Math.max(1.3, 7.5 * (1.0 - progress * 0.88));

      // Calculate perfect spiral helix coordinate for the ornament on this layer
      const targetAngle = y * 1.35;
      const targetX = Math.round(Math.cos(targetAngle) * (currentR - 0.45));
      const targetZ = Math.round(Math.sin(targetAngle) * (currentR - 0.45));

      for (let x = -Math.ceil(currentR); x <= Math.ceil(currentR); x++) {
        for (let z = -Math.ceil(currentR); z <= Math.ceil(currentR); z++) {
          const d = Math.hypot(x, z);
          if (d <= currentR && d > 0) { // Don't overwrite central trunk
            if (d > currentR - 0.8 && Math.random() < 0.2 && !(x === targetX && z === targetZ)) {
              continue; // Natural edge variation, but never skip the ornament!
            }
            
            // Place evenly spaced spiral ornaments/lights
            const isOrnament = (x === targetX && z === targetZ);
            if (isOrnament) {
              const colors = [0xff0055, 0x00ccff, 0xffcc00, 0xff00ff, 0x00ffcc, 0xffffff];
              const ornamentColor = colors[y % colors.length]!;
              voxels.push({
                x, y, z,
                color: ornamentColor,
                materialType: 'emissive'
              });
            } else {
              voxels.push({
                x, y, z,
                color: getRandomizedColor(PALETTE.LEAF_PINE_DARK, 0.03),
                materialType: 'foliage'
              });
            }
          }
        }
      }
    }

    // Top tip peak needles (attached directly to trunk peak, but under the star)
    voxels.push({ x: 0, y: 21, z: 0, color: PALETTE.LEAF_PINE_DARK, materialType: 'foliage' });

    // Gorgeous 3D Star Topper at the peak (replaces the flat cross and brown pole)
    // Star center at y = 22, sitting directly above the foliage tip (y = 21)
    voxels.push({ x: 0, y: 22.2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: 0.9 });
    
    // Thin gold connection rod down to green peak
    voxels.push({ x: 0, y: 21.5, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.25, scaleZ: 0.25, scaleY: 0.6 });
    
    // Star Points:
    // Top spike (tallest and tapered)
    voxels.push({ x: 0, y: 23.1, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.45, scaleZ: 0.45, scaleY: 0.8 });
    voxels.push({ x: 0, y: 23.7, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.22, scaleZ: 0.22, scaleY: 0.5 });
    
    // Horizontal spikes (tapered outwards on X-axis)
    voxels.push({ x: -0.8, y: 22.2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.6, scaleY: 0.35, scaleZ: 0.35 });
    voxels.push({ x: -1.3, y: 22.2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.3, scaleY: 0.18, scaleZ: 0.18 });
    
    voxels.push({ x: 0.8, y: 22.2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.6, scaleY: 0.35, scaleZ: 0.35 });
    voxels.push({ x: 1.3, y: 22.2, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleX: 0.3, scaleY: 0.18, scaleZ: 0.18 });
    
    // Horizontal spikes (tapered outwards on Z-axis)
    voxels.push({ x: 0, y: 22.2, z: 0.8, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleZ: 0.6, scaleY: 0.35, scaleX: 0.35 });
    voxels.push({ x: 0, y: 22.2, z: 1.3, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleZ: 0.3, scaleY: 0.18, scaleX: 0.18 });
    
    voxels.push({ x: 0, y: 22.2, z: -0.8, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleZ: 0.6, scaleY: 0.35, scaleX: 0.35 });
    voxels.push({ x: 0, y: 22.2, z: -1.3, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scaleZ: 0.3, scaleY: 0.18, scaleX: 0.18 });
    
    // Small diagonal flares to give an 8-pointed sparkling star shape
    const diagScale = 0.32;
    voxels.push({ x: 0.45, y: 22.6, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: -0.45, y: 22.6, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: 0, y: 22.6, z: 0.45, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: 0, y: 22.6, z: -0.45, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    
    voxels.push({ x: 0.45, y: 21.8, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: -0.45, y: 21.8, z: 0, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: 0, y: 21.8, z: 0.45, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
    voxels.push({ x: 0, y: 21.8, z: -0.45, color: PALETTE.LAMP_GOLD, materialType: 'emissive', scale: diagScale });
  }

  return {
    id,
    name,
    category: 'tree',
    description: `High-resolution micro-voxel ${type.replace('_', ' ')} tree`,
    voxels,
    dimensions: { x: 16, y: 24, z: 16 }
  };
}
